import { format } from "date-fns";
import { rootStore } from "src/app";
import { theme } from "src/config";
import client from "src/feathers";
import { DataType, IComment, IItem, ILog, ItemType, Services } from "src/types";
import styled from "styled-components";
import { Trans, TransProps } from "react-i18next";
import React, { ReactNode } from "react";
import { Space, Tag, User } from "src/models";
import i18n from "src/i18n";



export class LogParser {

  public static readonly HighlightedText = styled.span`
    font-weight:    600;
    color:          ${theme.color.slate12};
    text-transform: lowercase;
    text-overflow:  ellipsis;
    white-space:    nowrap;
    overflow:       hidden;
  `;
  constructor(private _DEBUG?: boolean) { };

  private static escapeTags = (str: string): string => {
    return str.replace("<", "&lt;").replace(">", "&gt;");
  }

  private getRoomName = async (room_id: number): Promise<string> => {
    if(!room_id) return "";
    let r: Space = rootStore.spaceStore.getItemByID(room_id);
    if(!r) {
      try {
        r = Space.create(await client.service(Services.Space).get(room_id));
      } catch(err) {
        console.error("Failed to get room information to generate log", room_id);
        return "";
      }
    }
    return LogParser.escapeTags(r.title);
  }

  private getUserName = async (user_id: number): Promise<string> => {
    if(!user_id) return "";
    let u: User = rootStore.userStore.container[user_id];
    if(!u) {
      try {
        u = User.create(await client.service(Services.Users).get(user_id));
      } catch(err) {
        console.error("Failed to get user information to generate log", user_id);
        return "";
      }
    }
    return LogParser.escapeTags(u.getFullName);
  }

  private getItemNumber = async (item_id: number, type: ItemType): Promise<number> => {
    try {
      // Check if the item is loaded in the store, load it from the backend otherwise
      const i: IItem = 
        rootStore.itemStores[type].container[item_id] ||
        await client.service(type).get(item_id);

      return i.number;
    } catch (err) {
      console.error("Failed to get item number", item_id, err);
      throw new Error("Failed to get item number");
    }
  }

  private getChildRoomName = async (item_id: number): Promise<string> => {
    try {
      const room_id: number = (await client.service(Services.Question).get(item_id)).room_id;
      return await this.getRoomName(room_id);
    } catch(err) {
      console.error("Failed to load child room name", item_id);
      throw new Error("Failed to load child room name");
    }
  }

  private isUserLinkEvent = (log: ILog): boolean => {
    return ["watcher", "participant"].includes(log.property);
  }

  private isItemLinkEvent = (log: ILog): boolean => {
    return ["question", "deliverable", "meeting", "milestone"].includes(log.property);
  }
  private isTagLinkEvent = (log: ILog): boolean => {
    return ["tag"].includes(log.property);
  }

  private isUserAssign = (log: ILog): boolean => {
    return ["to", "from", "leader_id"].includes(log.property) && log.data && log.data !== "null";
  }

  public parse = async (log: ILog, highlight: boolean = true): Promise<TransProps<any, "log">> => {
    const keyPrefix: string = highlight ? "h_" : "";
    
    if(!log) return {};
    const { data_type, property, event_type } = log;

    this._DEBUG && console.log("Parsing log entry", log);
    
    // User action: Start / Close Meeting
    if(data_type === DataType.Space) {
      if(["created", "deleted"].includes(log.event_type)) {
        const data: {[key: string]: any} = log.data as {[key: string]: any};
        const user_name: string = await this.getUserName(data.user_id);
        const added = log.event_type === "created" ? "add" : "remove";
        return {
          shouldUnescape: true,
          i18nKey:    keyPrefix + (added ? "join_space" : "leave_space"),
          ns:         "log",
          components: {em: <LogParser.HighlightedText />},
  
          values     : {
            user_name: user_name,
            role:      data.role, // Possibly empty
            context:   data.space_type
          }
        }
      } else { // start / close
        const item_number: number = await this.getItemNumber(log.item_id, DataType.Meeting);
        const user_name:   string = await this.getUserName(log.user_id);
        return {
          shouldUnescape: true,
          i18nKey:    keyPrefix + "log_meeting",
          ns:         "log",
          components: {em: <LogParser.HighlightedText />},
          values     : {
            user_name,
            item_number,
            context: log.event_type
          }
        }
      }
    // User action: Enter / Leave Meeting
    } else if(data_type === DataType.User) {
      const user_name: string = await this.getUserName(log.user_id);
      if(log.meeting_id) {
        return  { 
          shouldUnescape: true,
          i18nKey:    keyPrefix + "meeting_attendance",
          ns:         "log",
          components: {em: <LogParser.HighlightedText />},
          values:     {
            user_name: user_name,
            context:   event_type
          }
        };
      } else { // normal enter room
        const room_name: string = await this.getRoomName(log.room_id);
        return  { 
          shouldUnescape: true,
          i18nKey:    keyPrefix + "enter_room",
          ns:         "log",
          components: {em: <LogParser.HighlightedText />},
          values:     {
            user_name,
            room_name,
            context: event_type
          }
        };
      } 
    
    // User Link added or removed
    } else if(this.isUserLinkEvent(log)) {
      const added = log.event_type === "created" ? "add" : "remove";
      const user_name:   string = await this.getUserName((log.data as {user_id: number}).user_id);
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "user_link",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          context:     added,
          link_type:   property,
          user_name:   user_name,
          item_type:   data_type,
          item_number: item_number,
        }
      };
    // Tags added or removed
    } else if(this.isTagLinkEvent(log)) {
      const added = log.event_type === "created" ? "add" : "remove";
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      const tag: Tag = rootStore.tagStore.container[(log.data as {tag_id: number}).tag_id];
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "tag_link",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          context:     added,
          item_type:   log.data_type,
          item_number: item_number,
          tag:         LogParser.escapeTags(tag?.title || i18n.t("removed_tag")),
        }
      };
    // Item Link Added or removed
    } else if(this.isItemLinkEvent(log)) {
      const added = log.event_type === "created" ? "add" : "remove";
      const item_number1: number = await this.getItemNumber((log.data as {item_id: number}).item_id, property as ItemType);
      const item_number2: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "item_link",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          context:      added,
          item_type1:   property,
          item_number1: item_number1,
          item_type2:   data_type,
          item_number2: item_number2,
        }
      };

    // User Assign
    } else if(this.isUserAssign(log)) {
      const prop = property == 'leader_id' ? 'leader' : property;
      const user_name:   string = await this.getUserName(log.data as number);
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "assign_user",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          user_name:   user_name,
          item_type:   data_type,
          item_number: item_number,
          prop:        i18n.t(prop, {ns: 'prop'}).toLowerCase(),
        }
      };
    // Comment added or removed
    } else if(log.property === "comment") {
      const added = log.event_type === "created" ? "add" : "remove";
      const user_name:   string   = await this.getUserName(log.user_id as number);
      const item_number: number   = await this.getItemNumber(log.item_id, data_type as ItemType);
      const comment:     IComment = log.data as unknown as IComment;

      return { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "comment" + (comment.private ? "_private" : ""),
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          user_name:   user_name,
          item_number: item_number,
          item_type:   data_type,
          context:     added,
        }
      };
    // Item Created
    } else if(log.event_type === "created") {
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "create_item",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          item_type:   data_type,
          item_number: item_number,
        }
      };
      
    // Item Removed
    } else if(log.event_type === "deleted") {
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "remove_item",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          item_type:   data_type,
          item_number: item_number,
        }
      };
    // Remit question
    } else if(log.event_type === "changed" && log.property === "child_id") {
      const user_name:   string = await this.getUserName(log.user_id as number);
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      // Cancelled remittance 
      if(!log.data) {
        return  { 
          shouldUnescape: true,
          i18nKey:    keyPrefix + "remit_cancel",
          ns:         "log",
          components: {em: <LogParser.HighlightedText />},
          values:     {
            user_name,
            item_number,
            ns: "log"
          }
        };
      }
      const room_name:   string = await this.getChildRoomName(Number(log.data))
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "remit",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          user_name,
          item_number,
          room_name,
          ns: "log"
        }
      };
    // Relocate item to new room
    } else if(log.event_type === "changed" && log.property === "room_id") {
      const user_name:   string = await this.getUserName(log.user_id as number);
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      const room_name:   string = await this.getRoomName(Number(log.data))
      return  { 
        shouldUnescape: true,
        i18nKey:    keyPrefix + "relocate",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          prop:        property,
          item_type:   data_type,
          user_name,
          item_number,
          room_name,
          ns: "log"
        }
      };
    // Set Property
    } else {
      
      const item_number: number = await this.getItemNumber(log.item_id, data_type as ItemType);
      
      let value_str = JSON.stringify(log.data).replace(/^"|"$/g, ''); // Remove excess ""
      value_str = value_str.replace(/<[^>]+>/g, ''); // Strip HTML

      let prop = property;

      switch( value_str.toString() ) {
        case 'true'   : value_str = i18n.t('yes', {ns: 'prop'}); break;
        case 'false'  : value_str = i18n.t('no', {ns: 'prop'});  break;
        case 'null'   : value_str = "";  break;
      }

      switch( property ) {
        case 'answered' : value_str = value_str ? i18n.t('yes', {ns: 'prop'}) : i18n.t('no', {ns: 'prop'}); break;
        case 'position' : value_str = i18n.t(value_str, {ns: 'prop'}); break;
        case 'progress' : value_str = value_str + '%'; break;
        case "date":
        case "start": 
        case "start_date": 
        case "end_date": 
        case "opened": 
        case "postponed":
        case "delay_date" :
        value_str = format(new Date(log.data as string), "yyyy-MM-dd")
        break;
        case "leader_id" : value_str = ''; prop = 'leader'; break;
      }
      
      return  {
        shouldUnescape: true,
        i18nKey:    keyPrefix + "set_prop",
        ns:         "log",
        components: {em: <LogParser.HighlightedText />},
        values:     {
          prop:        i18n.t(prop, {ns: 'prop'}).toLowerCase(),
          item_type:   data_type,
          item_number: item_number,
          value:       value_str,
          ns: "log"
        }
      };
      
    }
  }
}